import React, { useState, useEffect, useRef } from 'react';
import { supabase, canAccess } from './supabase';
import { qboFetch } from './qbo';
import { Field, Input, Select, Textarea, FormGrid, SectionLabel, SaveMessage, Card, SubmitBtn, SecondaryBtn, STATES, INDUSTRIES, FW_TRIGGERS, REFERRAL_SOURCES, DISPATCH_STATUSES, ENTITY_LABELS } from './Shared';

const EMPTY = { company: '', company_id: '', property_id: '', property_name: '', contact_name: '', contact_email: '', contact_phone: '', site_address: '', state: '', industry: '', trigger: '', referral_source: '', status: 'Dispatched', repeat_client: 'no', notes: '', entity: '' };
const STORAGE_KEY = 'ao_dispatch_form';

function loadSaved() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : EMPTY;
  } catch { return EMPTY; }
}

// Searchable company dropdown
function CompanySearch({ value, companyId, onChange, onClear }) {
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const ref = useRef(null);

  useEffect(() => { setQuery(value || ''); }, [value]);

  useEffect(() => {
    if (!query || query.length < 1) { setResults([]); return; }
    const t = setTimeout(async () => {
      const { data } = await supabase.from('companies').select('id, name').ilike('name', `%${query}%`).eq('is_active', true).order('name').limit(20);
      setResults(data || []);
      setOpen(true);
    }, 200);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    function handleClick(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  async function addCompany() {
    if (!newName.trim()) return;
    const { data, error } = await supabase.from('companies').insert({ name: newName.trim() }).select('id, name').single();
    if (!error && data) {
      onChange(data.name, data.id);
      setQuery(data.name);
      setOpen(false);
      setAdding(false);
      setNewName('');
    }
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div style={{ display: 'flex', gap: '6px' }}>
        <input
          value={query}
          onChange={e => { setQuery(e.target.value); if (!e.target.value) onClear(); }}
          onFocus={() => query && setOpen(true)}
          placeholder="Search or type company name…"
          className="field-input"
          style={{ flex: 1 }}
        />
        {companyId && (
          <button type="button" onClick={onClear} className="btn btn-secondary btn-sm" style={{ whiteSpace: 'nowrap' }}>Clear</button>
        )}
      </div>
      {open && (results.length > 0 || query.length > 0) && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #E2E8F0', borderRadius: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 100, maxHeight: '200px', overflowY: 'auto' }}>
          {results.map(c => (
            <div key={c.id} onClick={() => { onChange(c.name, c.id); setQuery(c.name); setOpen(false); }}
              style={{ padding: '9px 12px', cursor: 'pointer', fontSize: '13px', borderBottom: '1px solid #F1F5F9' }}
              onMouseEnter={e => e.target.style.background = '#F8FAFC'}
              onMouseLeave={e => e.target.style.background = '#fff'}>
              {c.name}
            </div>
          ))}
          {!adding && (
            <div onClick={() => { setAdding(true); setNewName(query); setOpen(false); }}
              style={{ padding: '9px 12px', cursor: 'pointer', fontSize: '13px', color: '#3B82F6', borderTop: results.length ? '1px solid #E2E8F0' : 'none' }}>
              + Add "{query}" as new company
            </div>
          )}
        </div>
      )}
      {adding && (
        <div style={{ marginTop: '8px', display: 'flex', gap: '6px' }}>
          <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Company name" className="field-input" style={{ flex: 1 }} />
          <button type="button" onClick={addCompany} className="btn btn-primary btn-sm">Add</button>
          <button type="button" onClick={() => setAdding(false)} className="btn btn-secondary btn-sm">Cancel</button>
        </div>
      )}
    </div>
  );
}

// Property dropdown filtered by company
function PropertySelect({ companyId, value, onChange }) {
  const [properties, setProperties] = useState([]);
  const [adding, setAdding] = useState(false);
  const [newProp, setNewProp] = useState({ name: '', site_address: '', state: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!companyId) { setProperties([]); return; }
    supabase.from('properties').select('id, name, site_address, state').eq('parent_company_id', companyId).eq('is_active', true).order('name')
      .then(({ data }) => setProperties(data || []));
  }, [companyId]);

  async function addProperty() {
    if (!newProp.name.trim()) return;
    setSaving(true);
    const { data, error } = await supabase.from('properties')
      .insert({ name: newProp.name.trim(), site_address: newProp.site_address, state: newProp.state, parent_company_id: companyId, is_active: true })
      .select('id, name, site_address, state').single();
    setSaving(false);
    if (!error && data) {
      setProperties(p => [...p, data]);
      onChange(data.id, data.name, data.site_address, data.state);
      setAdding(false);
      setNewProp({ name: '', site_address: '', state: '' });
    }
  }

  if (!companyId) return <div style={{ fontSize: '12px', color: '#94A3B8', padding: '8px 0' }}>Select a company first</div>;

  return (
    <div>
      <div style={{ display: 'flex', gap: '6px' }}>
        <select value={value} onChange={e => {
          const prop = properties.find(p => p.id === e.target.value);
          if (prop) onChange(prop.id, prop.name, prop.site_address, prop.state);
          else onChange('', '', '', '');
        }} className="field-select" style={{ flex: 1 }}>
          <option value="">Select property…</option>
          {properties.map(p => <option key={p.id} value={p.id}>{p.name}{p.site_address ? ` — ${p.site_address}` : ''}</option>)}
        </select>
        <button type="button" onClick={() => setAdding(a => !a)} className="btn btn-secondary btn-sm" style={{ whiteSpace: 'nowrap' }}>
          {adding ? 'Cancel' : '+ New'}
        </button>
      </div>
      {adding && (
        <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '6px', padding: '10px', background: '#F8FAFC', borderRadius: '6px', border: '1px solid #E2E8F0' }}>
          <input value={newProp.name} onChange={e => setNewProp(p => ({ ...p, name: e.target.value }))} placeholder="Property name *" className="field-input" />
          <input value={newProp.site_address} onChange={e => setNewProp(p => ({ ...p, site_address: e.target.value }))} placeholder="Address" className="field-input" />
          <select value={newProp.state} onChange={e => setNewProp(p => ({ ...p, state: e.target.value }))} className="field-select">
            <option value="">State</option>
            {STATES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <button type="button" onClick={addProperty} disabled={saving} className="btn btn-primary btn-sm">{saving ? 'Saving…' : 'Add property'}</button>
        </div>
      )}
    </div>
  );
}

export default function DispatchForm({ profile }) {
  const [form, setForm] = useState(loadSaved);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ show: false, text: '', error: false });

  useEffect(() => {
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(form)); } catch {}
  }, [form]);

  const accessibleEntities = ['national', 'florida', 'newjersey'].filter(e => canAccess(profile, e)).map(e => ({ value: e, label: ENTITY_LABELS[e] }));
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  function clear() {
    setForm(EMPTY);
    try { sessionStorage.removeItem(STORAGE_KEY); } catch {}
  }

  async function save() {
    if (!form.company) { setMsg({ show: true, text: 'Company name is required.', error: true }); return; }
    if (!form.entity) { setMsg({ show: true, text: 'Please select an entity.', error: true }); return; }
    setLoading(true);
    const { error } = await supabase.from('dispatches').insert({ 
  ...form, 
  company_id: form.company_id || null,
  property_id: form.property_id || null,
  repeat_client: form.repeat_client === 'yes', 
  submitted_by: profile.id, 
  submitted_by_name: profile.full_name || profile.email 
});
    if (!error && form.contact_name) {
      await supabase.from('contacts').insert({ full_name: form.contact_name, company: form.company, email: form.contact_email, phone: form.contact_phone, state: form.state, contact_type: 'client', source_entity: form.entity, submitted_by: profile.id, submitted_by_name: profile.full_name || profile.email });
    }
    setLoading(false);
    if (error) { setMsg({ show: true, text: 'Error: ' + error.message, error: true }); return; }
    const declined = form.status.startsWith('Declined');
    setMsg({ show: true, text: 'Saved.' + (declined ? ' Decline recorded as market signal.' : ''), error: false });
    if (!declined) {
      const { data: saved } = await supabase.from('dispatches').select('id').order('created_at', { ascending: false }).limit(1).single();
      if (saved) {
        qboFetch('/qbo/estimate', {
          method: 'POST',
          body: JSON.stringify({ dispatch_id: saved.id, entity: form.entity, company_name: form.company, contact_name: form.contact_name, contact_email: form.contact_email })
        }).catch(() => {});
      }
    }
    clear();
    setTimeout(() => setMsg({ show: false }), 4000);
  }

  return (
    <Card>
      <SectionLabel>JOB DETAILS</SectionLabel>
      <FormGrid>
        <Field label="Entity">
          <Select value={form.entity} onChange={set('entity')} options={accessibleEntities} placeholder="Select entity" />
        </Field>
        <Field label="Client / company name">
          <CompanySearch
            value={form.company}
            companyId={form.company_id}
            onChange={(name, id) => setForm(f => ({ ...f, company: name, company_id: id }))}
            onClear={() => setForm(f => ({ ...f, company: '', company_id: '', property_id: '', property_name: '' }))}
          />
        </Field>
        <Field label="Property">
          <PropertySelect
            companyId={form.company_id}
            value={form.property_id}
            onChange={(id, name, address, state) => setForm(f => ({
              ...f,
              property_id: id,
              property_name: name,
              site_address: address || f.site_address,
              state: state || f.state
            }))}
          />
        </Field>
        <Field label="Site address">
          <Input value={form.site_address} onChange={set('site_address')} placeholder="123 Main St, Tampa FL" />
        </Field>
        <Field label="Contact name"><Input value={form.contact_name} onChange={set('contact_name')} placeholder="Jane Smith" /></Field>
        <Field label="Contact email"><Input type="email" value={form.contact_email} onChange={set('contact_email')} placeholder="jane@acme.com" /></Field>
        <Field label="Contact phone"><Input type="tel" value={form.contact_phone} onChange={set('contact_phone')} /></Field>
        <Field label="State"><Select value={form.state} onChange={set('state')} options={STATES} placeholder="Select state" /></Field>
      </FormGrid>

      <SectionLabel>MARKET INTELLIGENCE</SectionLabel>
      <FormGrid>
        <Field label="Industry vertical"><Select value={form.industry} onChange={set('industry')} options={INDUSTRIES} placeholder="Select industry" /></Field>
        <Field label="Fire watch trigger"><Select value={form.trigger} onChange={set('trigger')} options={FW_TRIGGERS} placeholder="Select trigger" /></Field>
        <Field label="Referral source"><Select value={form.referral_source} onChange={set('referral_source')} options={REFERRAL_SOURCES} placeholder="Select source" /></Field>
        <Field label="Job status"><Select value={form.status} onChange={set('status')} options={DISPATCH_STATUSES} /></Field>
        <Field label="Repeat client?"><Select value={form.repeat_client} onChange={set('repeat_client')} options={[{ value: 'no', label: 'No' }, { value: 'yes', label: 'Yes' }]} /></Field>
      </FormGrid>

      <div style={{ marginTop: '14px' }}>
        <Field label="Notes"><Textarea value={form.notes} onChange={set('notes')} placeholder="Any relevant context..." /></Field>
      </div>

      <div className="form-actions">
        <SecondaryBtn onClick={clear}>Clear</SecondaryBtn>
        <SubmitBtn onClick={save} loading={loading}>Save dispatch record</SubmitBtn>
      </div>
      <SaveMessage {...msg} />
    </Card>
  );
}

